/**
 * Multi-step custom output editor for gesture bindings / saved recipes.
 */

import { memo } from "react";
import type { ActionThen } from "../engines/types";
import type { WalletSnapshot } from "../suite/types";
import {
  STEP_KINDS,
  TEMPLATE_HINT,
  extractStepFields,
  stepKindToThen,
  thenToStepKind,
  type StepKindId,
} from "../actions/actionCatalog";
import { VideoHandoffFields } from "./VideoHandoffFields";

type Props = {
  steps: ActionThen[];
  onChange: (steps: ActionThen[]) => void;
  /** Compact mode for sidebar per-gesture */
  compact?: boolean;
  label?: string;
  onLabelChange?: (label: string) => void;
  showLabel?: boolean;
  wallet?: WalletSnapshot | null;
};

export const CustomOutputBuilder = memo(function CustomOutputBuilder({
  steps,
  onChange,
  compact,
  label,
  onLabelChange,
  showLabel,
  wallet = null,
}: Props) {
  const updateStep = (index: number, next: ActionThen) => {
    const copy = steps.slice();
    copy[index] = next;
    onChange(copy);
  };

  const setStepKind = (index: number, kind: StepKindId) => {
    const prev = extractStepFields(steps[index] || { kind: "toast" });
    updateStep(index, stepKindToThen(kind, prev));
  };

  const patchFields = (
    index: number,
    patch: Partial<ReturnType<typeof extractStepFields>>,
  ) => {
    const step = steps[index];
    if (!step) return;
    const kind = thenToStepKind(step);
    const fields = { ...extractStepFields(step), ...patch };
    updateStep(index, stepKindToThen(kind, fields));
  };

  const removeStep = (index: number) => {
    onChange(steps.filter((_, i) => i !== index));
  };

  const moveStep = (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= steps.length) return;
    const copy = steps.slice();
    [copy[index], copy[j]] = [copy[j], copy[index]];
    onChange(copy);
  };

  const addStep = () => {
    onChange([
      ...steps,
      stepKindToThen("toast", { text: "{{gesture_label}}" }),
    ]);
  };

  return (
    <div className={`vf-custom-builder${compact ? " vf-custom-builder--compact" : ""}`}>
      {showLabel && (
        <input
          className="vf-input vf-binding-param"
          placeholder="Output name"
          value={label || ""}
          onChange={(e) => onLabelChange?.(e.target.value)}
        />
      )}
      <div className="vf-binding-hint">{TEMPLATE_HINT}</div>
      {steps.length === 0 && (
        <div className="vf-cost-hint">No steps yet — add one below.</div>
      )}
      {steps.map((step, i) => {
        const kind = thenToStepKind(step);
        const meta = STEP_KINDS.find((s) => s.id === kind);
        const fields = extractStepFields(step);
        return (
          <div key={i} className="vf-custom-step">
            <div className="vf-custom-step-head">
              <span className="vf-custom-step-n">{i + 1}</span>
              <select
                className="vf-select vf-binding-select"
                value={kind}
                onChange={(e) => setStepKind(i, e.target.value as StepKindId)}
              >
                {STEP_KINDS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="vf-chip vf-chip-sm"
                title="Move up"
                disabled={i === 0}
                onClick={() => moveStep(i, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="vf-chip vf-chip-sm"
                title="Move down"
                disabled={i >= steps.length - 1}
                onClick={() => moveStep(i, 1)}
              >
                ↓
              </button>
              <button
                type="button"
                className="vf-chip vf-chip-sm"
                title="Remove step"
                onClick={() => removeStep(i)}
              >
                ×
              </button>
            </div>
            {meta?.fields?.includes("text") && (
              <input
                className="vf-input vf-binding-param"
                placeholder={
                  kind === "forge_video"
                    ? "Video prompt · {{gesture_label}}"
                    : kind === "forge_image"
                      ? "Image prompt · {{gesture_label}}"
                      : "Text / {{gesture_label}}"
                }
                value={fields.text || ""}
                onChange={(e) => patchFields(i, { text: e.target.value })}
              />
            )}
            {(kind === "forge_video" || kind === "forge_image") && (
              <VideoHandoffFields
                purpose={kind === "forge_image" ? "image" : "video"}
                params={{
                  prompt: fields.prompt || fields.text,
                  autogen: fields.autogen,
                  provider: fields.provider,
                  pick: fields.pick,
                  model: fields.model,
                  sku: fields.sku,
                  rating: fields.rating,
                }}
                wallet={wallet}
                onChange={(params) => patchFields(i, params)}
              />
            )}
            {meta?.fields?.includes("url") && (
              <input
                className="vf-input vf-binding-param"
                type="url"
                placeholder="https://… or with {{gesture}}"
                value={fields.url || ""}
                onChange={(e) => patchFields(i, { url: e.target.value })}
              />
            )}
            {meta?.fields?.includes("keys") && (
              <input
                className="vf-input vf-binding-param"
                placeholder="Enter, ArrowUp, Ctrl+S…"
                value={fields.keys || ""}
                onChange={(e) => patchFields(i, { keys: e.target.value })}
              />
            )}
            {meta?.fields?.includes("channel") && (
              <input
                className="vf-input vf-binding-param"
                placeholder="visionforge.custom"
                value={fields.channel || ""}
                onChange={(e) => patchFields(i, { channel: e.target.value })}
              />
            )}
            {meta?.fields?.includes("frequency") && (
              <input
                className="vf-input vf-binding-param"
                type="number"
                min={200}
                max={2000}
                step={20}
                placeholder="Hz"
                value={fields.frequency ?? 880}
                onChange={(e) =>
                  patchFields(i, { frequency: Number(e.target.value) || 880 })
                }
              />
            )}
            {meta?.fields?.includes("dy") && (
              <input
                className="vf-input vf-binding-param"
                type="number"
                min={50}
                max={2000}
                step={50}
                placeholder="Pixels"
                value={fields.dy ?? 420}
                onChange={(e) => patchFields(i, { dy: Number(e.target.value) || 420 })}
              />
            )}
          </div>
        );
      })}
      <button type="button" className="vf-chip" onClick={addStep}>
        + Add step
      </button>
    </div>
  );
});
